import { parse, ParseResult } from 'papaparse'
import { z } from 'zod'

import { AddUserProps } from '@img-arena/consoles-api-dde'
import { CircularProgress } from '@mui/material'
import { createBulkHandler } from '@/service/bulkOperationsService'
import { ddeApi } from '@/service/ddeApi'
import { notifyError } from '@/utils/notifyError'

export const IMPORT_STEPS = {
  UPLOAD: 'upload',
  PREVIEW: 'preview',
  UPLOADING: 'uploading'
} as const

export type ImportStep = (typeof IMPORT_STEPS)[keyof typeof IMPORT_STEPS]

export interface CSVUser {
  email: string
  name: string
}

export interface CSVParseResult {
  validUsers: CSVUser[]
  invalidUsers: CSVUser[]
}

export interface UploadProgress {
  total: number
  success: number
  failed: number
  current: number
}

// https://owasp.org/www-community/attacks/CSV_Injection
const INVALID_STARTING_CHARS = {
  '=': '=',
  '+': '+',
  '-': '-',
  '@': '@',
  '\t': 'Tab',
  '0x09': '0x09',
  '\n': 'Newline',
  '\r': 'Newline',
  '0x0D': '0x0D'
} as const

export const parseCSVFile = (file: File): Promise<CSVUser[]> => {
  return new Promise((resolve, reject) => {
    parse(file, {
      header: true,
      skipEmptyLines: true,
      transform: (cell, col) => {
        const badCharacterIndex = Object.keys(INVALID_STARTING_CHARS).findIndex((val) =>
          cell.startsWith(val)
        )
        if (badCharacterIndex > -1) {
          const badCharacter = Object.keys(INVALID_STARTING_CHARS)[
            badCharacterIndex
          ] as keyof typeof INVALID_STARTING_CHARS
          const errorMessage = `Invalid character(s) found in ${col}: ${cell}. Please remove invalid character: ${INVALID_STARTING_CHARS[badCharacter]}`
          notifyError(errorMessage)
          reject(new Error(errorMessage))
          return cell
        } else {
          return cell
        }
      },
      transformHeader: (value: string) => value.trim().replace(/\s+/g, '_').toLowerCase(),
      complete: (results: ParseResult<CSVUser>) => {
        resolve(results.data)
      },
      error: (error) => {
        reject(error)
      }
    })
  })
}

export const validateCSVUsers = (parsedUsers: CSVUser[]): CSVParseResult => {
  const validUsers: CSVUser[] = []
  const invalidUsers: CSVUser[] = []

  const emailSchema = z.string().email()

  parsedUsers.forEach((user) => {
    const hasName = user.name && user.name.trim().length > 0
    const isValidEmail = emailSchema.safeParse(user.email).success

    if (hasName && isValidEmail) {
      validUsers.push(user)
    } else {
      invalidUsers.push(user)
    }
  })

  return { validUsers, invalidUsers }
}

export const createUser = async (user: CSVUser, operatorId: number): Promise<void> => {
  const userData: AddUserProps['data'] = {
    emailAddress: user.email,
    userName: user.name,
    role: {
      operatorId,
      roleName: 'Operator'
    }
  }

  await ddeApi.endpoints.users.add(userData)
}

export const createUserUploadHandler = (
  operatorId: number,
  refreshData: () => Promise<void> | void
) => {
  return createBulkHandler({
    name: 'Upload Users',
    operation: (user: CSVUser) => createUser(user, operatorId),
    refreshData,
    getItemDisplayName: (user) => `${user.name} (${user.email})`
  })
}

export const filterExistingUsers = (
  csvUsers: CSVUser[],
  existingUsers: { userId: string }[]
): { newUsers: CSVUser[]; existingUsers: CSVUser[] } => {
  const existingEmails = new Set(existingUsers.map((user) => user.userId))

  const newUsers: CSVUser[] = []
  const duplicateUsers: CSVUser[] = []

  csvUsers.forEach((user) => {
    if (existingEmails.has(user.email)) {
      duplicateUsers.push(user)
    } else {
      newUsers.push(user)
    }
  })

  return {
    newUsers,
    existingUsers: duplicateUsers
  }
}

export const EXAMPLE_CSV_HEADERS = [
  { key: 'name', displayLabel: 'name' },
  { key: 'email', displayLabel: 'email' }
]

export const EXAMPLE_CSV_DATA = [
  { name: 'John Doe', email: 'john.doe@example.com' },
  { name: 'Jane Smith', email: 'jane.smith@example.com' },
  { name: 'Mike Johnson', email: 'mike.johnson@example.com' }
]

export interface DialogButtonProps {
  text: string
  disabled?: boolean
  onClick: () => void
  startIcon?: React.ReactNode
}

export const getSaveButtonProps = (
  step: ImportStep,
  newUsers: CSVUser[],
  handleUpload: () => void
): DialogButtonProps | undefined => {
  switch (step) {
    case IMPORT_STEPS.UPLOAD:
      return undefined
    case IMPORT_STEPS.PREVIEW:
      return {
        text: 'Upload Users',
        disabled: newUsers.length === 0,
        onClick: handleUpload
      }
    case IMPORT_STEPS.UPLOADING:
      return {
        text: 'Uploading...',
        disabled: true,
        onClick: () => {},
        startIcon: <CircularProgress size={16} />
      }
    default:
      return undefined
  }
}
